import React, { useEffect, useState } from 'react';
import Router, { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { t } from '@lingui/macro';
import isEqual from 'lodash/isEqual';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Box, Container } from '@components/layout/Grid';
import MetaTags from '@components/MetaTags';
import SearchBar from '@components/Search';
import VideoGenresList from '@ecosystems/VideoGenresList';
import VideosGenresFilters from '@ecosystems/video_library/filters/VideosGenresFilters';
import { GET_VIDEO_SETTINGS } from '@lib/apollo';
import useDebounce from '@lib/hooks/useDebounce';
import VideosGenresTaxonomiesSkeleton from '@skeletons/VideosGenresTaxonomiesSkeleton';
import {
  NewTaxonomyItemFieldsFragment,
  TaxonomiesV2Query,
  useTaxonomiesV2LazyQuery,
} from '@gql/generated';

const TAXONOMY_SLUGS = [
  'needs',
  'yoga-workout',
  'instructors',
  'skills',
  'health',
  'flavors',
  'bodyparts-needs',
  'durations',
  'accessories',
];

function sortTaxonomies(taxonomies: TaxonomiesV2Query['taxonomiesV2']) {
  return [...(taxonomies || [])].map((_item) => {
    const item = { ..._item };
    item.subcategories = item.subcategories
      ?.filter((subcategory) => !!subcategory)
      .map((subcategory) => {
        // subcategory.terms = subcategory.terms.map(term => { })
        const _subcategory = { ...subcategory };
        _subcategory.terms = [..._subcategory.terms].sort((term1, term2) => {
          if (item.slug === 'durations') {
            // parseInt() will extract the number at the start of the string
            let a = parseInt(term1.name);
            let b = parseInt(term2.name);
            return a > b ? 1 : -1;
          } else {
            let a = term1.name,
              b = term2.name;
            return a > b ? 1 : -1;
          }
        });
        return _subcategory;
      })
      .sort((a, b) => {
        return TAXONOMY_SLUGS.indexOf(a.slug) - TAXONOMY_SLUGS.indexOf(b.slug);
      });

    if (item.nonSubcategorized?.length > 0) {
      item.nonSubcategorized = [...item.nonSubcategorized].sort(
        (term1, term2) => {
          if (item.slug === 'durations') {
            // parseInt() will extract the number at the start of the string
            let a = parseInt(term1.name);
            let b = parseInt(term2.name);
            return a > b ? 1 : -1;
          } else {
            let a = term1.name,
              b = term2.name;
            return a > b ? 1 : -1;
          }
        }
      );
    }

    return item;
  });
}

const getSelectedTermsFromQuery = (q, taxonomies) => {
  const newSelectedTerms: Record<string, string[]> = {};
  Object.entries(q).forEach((entry) => {
    if (TAXONOMY_SLUGS.includes(entry[0]) && entry[1]) {
      newSelectedTerms[entry[0]] =
        entry[1] instanceof Array ? entry[1] : String(entry[1]).split(',');
    }
  });

  if (taxonomies) {
    const validTerms = Object.entries(newSelectedTerms).reduce(
      (acc, [key, values = []]) => {
        const group = taxonomies.find((term) => term.slug === key);

        if (group?.subcategories?.length > 0) {
          group?.subcategories.flat().forEach((taxonomySubcategory) => {
            return taxonomySubcategory.terms.forEach((term) => {
              if (
                values?.includes?.(term.slug) &&
                !acc[key]?.includes(term.slug)
              ) {
                acc[key] = [...(acc[key] || []), term.slug];
                return true;
              }
              return false;
            });
          });
        } else if (group?.nonSubcategorized?.length > 0) {
          group?.nonSubcategorized.forEach((term) => {
            if (
              values?.includes?.(term.slug) &&
              !acc[key]?.includes(term.slug)
            ) {
              acc[key] = [...(acc[key] || []), term.slug];
              return true;
            }
            return false;
          });
        }

        return acc;
      },
      {}
    );

    return validTerms;
  }

  return {};
};

const Qquery = (q) => {
  if (q) {
    return `?q=${encodeURIComponent(q)}`;
  }
  return '';
};

const taxesToQuery = (prefix, newSelectedTerms) => {
  let newQuery = '';
  Object.keys(newSelectedTerms).forEach((taxonomy, index) => {
    const terms = newSelectedTerms[taxonomy] || [];
    newQuery += `${taxonomy}=${terms.join(',')}`;
    if (index + 1 !== Object.keys(newSelectedTerms).length) {
      newQuery += '&';
    }
  });

  if (newQuery.length === 0) {
    return '';
  }

  return `${prefix}${newQuery}`;
};

const VideoGenres = () => {
  const router = useRouter();
  const query = router.query;
  const [genre, setGenre] = useState(query.genre || '');
  const [queryState, setQuery] = useState(query.q || '');
  const [taxonomies, setTaxonomies] = useState<NewTaxonomyItemFieldsFragment[]>(
    []
  );
  const [selectedTerms, setSelectedTerms] = useState<Record<string, string[]>>(
    {}
  );

  const debouncedSearchTerm = useDebounce(queryState, 500);
  const { lang } = query;

  const { loading: loadingVideoSettings, data: videoSettingsData } =
    useQuery(GET_VIDEO_SETTINGS);

  const [
    getTaxonomies,
    {
      called,
      loading: loadingTaxonomies,
      data: taxonomiesData,
      refetch: refetchTaxonomies,
    },
  ] = useTaxonomiesV2LazyQuery();

  useEffect(() => {
    const genre = query.genre as string;
    if (videoSettingsData?.videoSettings) {
      if (!called) {
        getTaxonomies({
          variables: {
            genre: genre === 'all' ? '' : genre,
            languages: videoSettingsData?.videoSettings.contentLanguages,
          },
        });
      } else {
        refetchTaxonomies({
          genre: genre === 'all' ? '' : genre,
          languages: videoSettingsData?.videoSettings.contentLanguages,
        });
      }
    }
  }, [videoSettingsData, query.genre]);

  useEffect(() => {
    if (taxonomiesData?.taxonomiesV2 && !loadingTaxonomies) {
      setTaxonomies(sortTaxonomies(taxonomiesData?.taxonomiesV2));
    }
  }, [taxonomiesData?.taxonomiesV2, loadingTaxonomies]);

  useEffect(() => {
    const QueryQ = query.q || '';
    const queryGenre = query.genre || '';
    if (
      debouncedSearchTerm !== QueryQ ||
      genre !== queryGenre ||
      !isEqual(
        selectedTerms,
        getSelectedTermsFromQuery(router.query, taxonomies)
      )
    ) {
      const search = Qquery(debouncedSearchTerm);
      const queryString = `${search}${taxesToQuery(
        search ? '&' : '?',
        selectedTerms
      )}`;
      Router.replace(
        `/[lang]/videos/genres/[genre]${queryString}`,
        `/${lang}/videos/genres/${genre}${queryString}`,
        { shallow: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, genre, selectedTerms]);

  useDeepCompareEffect(() => {
    if (query.genre !== genre) {
      setGenre(query.genre);
      setSelectedTerms({});
    } else if (
      !isEqual(
        selectedTerms,
        getSelectedTermsFromQuery(router.query, taxonomies)
      )
    ) {
      const queryTerms = getSelectedTermsFromQuery(router.query, taxonomies);
      setSelectedTerms(queryTerms);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, taxonomies]);

  const handleInputChangeSearchBar = (newValue) => {
    setQuery(newValue);
  };

  const clearAll = () => {
    setSelectedTerms({});
  };

  return (
    <>
      <MetaTags title={t`opengraph.videos_genres_title`} />
      <Container fullWidth px={[15, 20, 20]} py={[25, 30, 20]}>
        <Container flexDirection="column">
          {/* Search Bar */}
          <Box mb="20px">
            <SearchBar
              id="search_bar"
              onInputChange={handleInputChangeSearchBar}
              defaultInputValue={queryState}
            />
          </Box>
          {/* Taxonomies Menu */}
          {loadingVideoSettings || !called || loadingTaxonomies ? (
            <VideosGenresTaxonomiesSkeleton />
          ) : (
            <VideosGenresFilters
              key={genre as string}
              taxonomies={taxonomies}
              selectedTerms={selectedTerms}
              onChange={(...params) => {
                setSelectedTerms(...params);
              }}
            />
          )}
          {/* Filtered results metadata */}
          <VideoGenresList
            taxonomies={taxonomies}
            query={debouncedSearchTerm}
            genre={genre as string}
            selectedTerms={selectedTerms}
            clearAll={clearAll}
            renderTeachersSection={() => {
              return null;
            }}
          />
        </Container>
      </Container>
    </>
  );
};

export const getServerSideProps = (ctx) => {
  if (
    ctx.query.page &&
    // => when navigating from one page to another next calls 'getServerSideProps' on the new page (transition)
    // WE DONT DO REDIRECTION ON PAGE TRANSITION
    ctx.resolvedUrl === ctx.req.url
  ) {
    const url = new URL(ctx.req.url, process.env.DOMAIN);
    url.searchParams.delete('page');

    return {
      redirect: {
        destination: url.pathname + url.search,
        permanent: false,
      },
    };
  }

  return {
    props: {},
  };
};

export default VideoGenres;
